import type { ReactNode } from 'react';
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from '@headlessui/react';

/**
 * The shell every Model Catalog dialog shares (task 1102): a HeadlessUI
 * `Dialog`, so focus is trapped inside the panel while it is open and Escape
 * closes it. `busy` holds the dialog open while a request is in flight, so a
 * stray Escape cannot drop a half-submitted form.
 */
interface CatalogDialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: ReactNode;
  busy?: boolean;
  testId?: string;
  children: ReactNode;
}

export function CatalogDialog({ open, onClose, title, description, busy = false, testId, children }: CatalogDialogProps) {
  return (
    <Dialog open={open} onClose={busy ? () => {} : onClose} className="relative z-50">
      <DialogBackdrop className="fixed inset-0 bg-black/40" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <DialogPanel
          data-testid={testId}
          className="flex max-h-[calc(100vh-2rem)] w-full max-w-lg flex-col overflow-y-auto rounded-lg bg-white p-6 shadow-xl dark:bg-gray-800"
        >
          <DialogTitle className="text-lg font-medium text-gray-900 dark:text-white">{title}</DialogTitle>
          {description && <div className="mt-1 text-sm text-gray-600 dark:text-gray-300">{description}</div>}
          <div className="mt-4">{children}</div>
        </DialogPanel>
      </div>
    </Dialog>
  );
}

const BUTTON_BASE = 'inline-flex items-center justify-center rounded-md px-4 py-2 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed';

export const SECONDARY_BUTTON = `${BUTTON_BASE} border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600`;

export const PRIMARY_BUTTON = `${BUTTON_BASE} bg-indigo-600 text-white hover:bg-indigo-700`;
